import {
  Directive,
  Input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/core/app.state';
import { selectIsFieldVisible } from 'src/app/core/store/field-visibility/field-visibility.selectors';
import { FieldVisibilityModule } from 'src/app/core/store/field-visibility/field-visibility.types';

export interface FieldVisibilityContext {
  module: FieldVisibilityModule;
  field: string;
}

/**
 * Structural directive that renders its template only when the current user's
 * role does NOT hide the given (module, field) pair.
 *
 * Usage:
 *   <span *appFieldVisible="{ module: 'order', field: 'comment' }">...</span>
 *
 * The backend independently strips hidden keys from API responses; this
 * directive only governs the UI layout, not data exposure.
 */
@Directive({
  selector: '[appFieldVisible]',
  standalone: true,
})
export class HasFieldVisibilityDirective implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly context$ = new Subject<FieldVisibilityContext>();
  private hasView = false;

  constructor(
    private readonly templateRef: TemplateRef<unknown>,
    private readonly viewContainer: ViewContainerRef,
    private readonly store: Store<AppState>
  ) {
    this.context$
      .pipe(
        distinctUntilChanged(
          (a, b) => a.module === b.module && a.field === b.field
        ),
        switchMap((ctx) =>
          this.store.select(selectIsFieldVisible(ctx.module, ctx.field))
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((visible) => this.updateView(visible));
  }

  @Input()
  set appFieldVisible(context: FieldVisibilityContext) {
    if (!context || !context.module || !context.field) {
      return;
    }
    this.context$.next(context);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView(visible: boolean): void {
    if (visible && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
      return;
    }
    if (!visible && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}
